import { useEffect } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { routes } from '@/config/routes'
import type { RouteConfig } from '@/types'

/**
 * What the browser tab says.
 *
 * Set from one place off the route table rather than by each page, so a new
 * screen cannot be added with the previous page's title still showing. It is
 * also the name of the thing in a customer's tab bar, their history and any
 * bookmark, so "customer-portal-frontend" on every page was three places where
 * one tab looked exactly like the next.
 */
export const APP_NAME = 'My Portal'

/** Ordered so the catch-all is only reached when nothing else matches. */
const ALL_ROUTES: RouteConfig[] = [...routes.auth, ...routes.protected, ...routes.error]

/** "My Portal - Products", or just "My Portal" if a route has no name at all. */
export function titleForPath(pathname: string): string {
  const matched = ALL_ROUTES.find(
    (route) => route.path !== '*' && matchPath({ path: route.path, end: true }, pathname)
  )

  // Anything unmatched is the 404 page, so the tab should say so rather than
  // keep whichever title happened to be there when the URL was typed.
  const fallback = ALL_ROUTES.find((route) => route.path === '*')
  const route = matched ?? fallback

  // A full custom title (DF-338) is used exactly as given, for pages with a
  // specific SEO title. Everything else keeps the "My Portal - X" format.
  if (route?.documentTitle) return route.documentTitle

  const page = route?.title ?? route?.name
  return page ? `${APP_NAME} - ${page}` : APP_NAME
}

/**
 * DF-338: point the canonical link at the current URL, so the trailing-slash
 * root is the preferred version of the two duplicate root URLs the SEO audit
 * flagged. Self-referencing per page; falls back gracefully if the head can't
 * be touched.
 */
function setCanonical(pathname: string): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return

  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'canonical'
    document.head.appendChild(link)
  }
  link.href = `${window.location.origin}${pathname}`
}

/**
 * Keep the tab title in step with the route.
 *
 * Mounted once, inside the router. Client-side navigation never touches the
 * document title on its own, so without this the tab keeps whatever the first
 * page load set for the whole session.
 */
export function useDocumentTitle(): void {
  const { pathname } = useLocation()

  useEffect(() => {
    document.title = titleForPath(pathname)
    setCanonical(pathname)
  }, [pathname])
}

export default useDocumentTitle
